import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
// Only the error PARSERS are borrowed from the api module -- not its axios
// instance, which would attach a bearer token this page must never send.
import { NON_FIELD_KEY, getErrorMessage, getFieldErrors } from '@/services/api';
import type { QuoteStatus } from '@/types';
import { formatCurrency, formatDate } from '@/utils';
import { coverLabel, optionPremium } from '@/utils/coverTypes';
import type { AddonCover } from '@/utils/addons';
import { AdjustQuotePanel, type CustomiseState } from './AdjustQuotePanel';
import { QuoteComparison } from './QuoteComparison';
import { purchaseAfterAcceptance } from './PurchasePanel';
import { StartNotes, StartOptions, startFromState } from './StartNotes';

/**
 * B1: the shareable quote link.
 *
 * `QuoteRequestViewSet.share_link` mints `/public/quotes/<token>` and the
 * backend has served the matching `/api/public/quotes/<token>/` since BROKER-7 --
 * but the SPA never registered a route for that path, so every shared link fell
 * through to NotFoundPage. That is the reported 404; nothing on the backend was
 * broken.
 *
 * Unauthenticated by design, so it uses bare `axios` against
 * `/policy/api/public/` rather than the `@/services/api` instance, which would
 * attach a bearer token and run the refresh interceptor. Same approach as
 * BrokerProfile and InsurersDirectory.
 *
 * The payload is `PublicQuoteRequestSerializer`, deliberately minimised in
 * commit e4fb180 -- no broker/POSP ids, no notes, no activity, no commission.
 * Anyone holding the link can read this page, so if a field you want is absent
 * from the type below, its absence is the security decision, not an oversight.
 *
 * Wave D (D2/D5) turned this into the customer's whole journey: choose an
 * option, submit KYC, wait for approval, and later come back for the policy.
 * WHICH STAGE IS SHOWN IS THE SERVER'S DECISION -- `customer_state` -- not a
 * rule re-derived here from `status`. An expired link never reaches this
 * component at all: the API drops it from the queryset and the fetch 404s, so
 * there is nothing rendered to hide.
 */

export interface PublicQuoteOption {
  id: string;
  insurer_name: string;
  insurer_logo?: string | null;
  /** DERIVED server-side (`QuoteListSerializer.get_pricing_source`): the
   *  insurer's own API priced it, or the advisor entered it by hand. Optional
   *  because an older server does not send it -- then no badge is drawn. */
  pricing_source?: 'insurer_api' | 'manual';
  /** Which cover this option priced (`comprehensive | tp_only | saod`), and
   *  the server's label for it. One insurer can price the same car all three
   *  ways, so without these the cards are indistinguishable. Null for a
   *  premium the advisor typed in by hand. Optional: an older server omits them. */
  cover_type?: string | null;
  cover_label?: string | null;
  premium_breakdown?: { od_premium?: number | null; tp_premium?: number | null } | null;
  product_name?: string | null;
  premium_total?: string | number | null;
  premium_gross?: string | number | null;
  /** The cover amount the option quotes -- a health plan's sum insured. */
  sum_insured?: string | number | null;
  /** Queue #219: the attachments belonging to THIS option, not to the request. */
  documents?: PublicQuoteDocument[];
  cover_start?: string | null;
  cover_end?: string | null;
  status_display?: string;
  /** The IDV this price used and the insurer's accepted band (motor). */
  idv?: string | number | null;
  min_idv?: string | number | null;
  max_idv?: string | number | null;
  idv_adjusted?: boolean;
  /** Per requested add-on: included / not offered / not online. */
  addon_cover?: AddonCover | null;
}

/** Customer-facing attachment. The API exposes only quote_pdf / comparison_pdf
 *  here; the issued policy has its own list (`policy_documents`). */
export interface PublicQuoteDocument {
  id: string;
  name: string;
  document_type: string;
  document_type_display: string;
  file_size: number | null;
}

/** What the customer has already chosen and told us, if anything. */
interface PublicQuoteAcceptance {
  quote: string;
  accepted_at: string;
  payment_declared: boolean;
  payment_reference: string;
  payment_date: string | null;
  payment_mode: string;
}

/** Which identifiers this proposer must submit, and what has happened to them.
 *  Never the numbers themselves -- see `PublicQuoteRequestSerializer.get_kyc`. */
export interface PublicQuoteKyc {
  required_fields: KycField[];
  /** Whether the API would accept a submission right now, decided by the same
   *  transition table the endpoint enforces. Legacy rungs (`processing`,
   *  `submitted`) cannot enter KYC directly, so drawing the form for them
   *  would guarantee a 400 on every attempt. */
  can_submit: boolean;
  submitted: boolean;
  submitted_at: string | null;
  rejection_reason: string;
  message: string;
}

/** The stage the SERVER says this reader is at. */
export type PublicQuoteState =
  | 'open'
  | 'kyc_under_review'
  | 'kyc_rejected'
  | 'awaiting_payment_link'
  | 'awaiting_policy'
  | 'policy_available';

type KycField = 'aadhaar_number' | 'pan_number' | 'gst_number';

/** The one Pay button's journey (`PublicQuoteRequestSerializer.get_purchase`).
 *  Null on a legacy rung, which keeps the declare-payment page. Flags only --
 *  the PAN, images and address never come back to the page. */
export interface PublicPurchase {
  payment_gate: 'immediate' | 'after_approval';
  /** True: the details form + Pay button. False: a product line with no online
   *  path -- the page says the advisor will send a link. (A motor option that
   *  cannot be bought online gets `purchase: null` -- the legacy page.) */
  online: boolean;
  /** Absent in hand-over mode (`handoff`). */
  details?: {
    submitted: boolean;
    submitted_at: string | null;
    kyc_method: '' | 'pan' | 'ckyc' | 'documents';
    editable: boolean;
    locked_reason: string;
    vehicle_numbers_on_file: boolean;
  };
  /** #593: life, the insurer finishes the sale on its own site. The page collects the proposal
   *  (`life`: names and masked ids only), then sends the customer there. */
  handoff?: boolean;
  /** #585: the insurer takes a browser Form-POST and collects the details itself: no proposal form here. */
  form_post?: boolean;
  life?: {
    submitted: boolean;
    submitted_at: string | null;
    sent_to_insurer: boolean;
    parties: { role: string; name: string; relationship: string; share_pct: string | null; pan_masked: string }[];
  };
}

export interface PublicQuoteData {
  request_number: string;
  broker_name: string;
  insurance_type_name: string;
  business_type: string;
  /** The raw lifecycle rung. Shown only as `status_display`; every BRANCH on
   *  this page reads `customer_state` instead, so the journey has one owner. */
  status: QuoteStatus;
  status_display: string;
  customer_state: PublicQuoteState;
  customer_message: string;
  contact_name: string;
  contact_phone: string;
  proposer_type: string;
  proposer_name: string | null;
  registration_number: string | null;
  sum_insured: string | number | null;
  expires_at: string | null;
  quotes: PublicQuoteOption[];
  documents: PublicQuoteDocument[];
  policy_documents: PublicQuoteDocument[];
  kyc: PublicQuoteKyc;
  acceptance: PublicQuoteAcceptance | null;
  /** What live pricing said when a visitor started this quote on the public
   *  site -- plain sentences only (`PublicQuoteRequestSerializer
   *  .get_pricing_summary`). Null for a quote a broker raised. */
  pricing_summary?: PublicPricingSummary | null;
  /** Absent on an older server; null on a legacy rung. */
  purchase?: PublicPurchase | null;
  /** `motor` | `health` | `travel` | ... -- which comparison to draw
   *  (`PublicQuoteRequestSerializer.get_product_line`). Optional: an older
   *  server omits it, and the page then treats the quote as motor. */
  product_line?: string | null;
  /** "Adjust your quote": the customer's IDV / claim / NCB / add-on choices
   *  and whether they may still re-price. Absent on an older server. */
  customise?: CustomiseState | null;
  created_at: string;
}

interface PublicPricingSummary {
  status: string;
  /** Priced unit by unit: `pricing` while any insurer x cover is still being
   *  asked (poll fast), `done` once every one has settled. Absent on a quote
   *  priced the older, all-at-once way. */
  state?: 'pricing' | 'done';
  units?: {
    insurer_code: string;
    insurer_name: string;
    cover_type: string;
    state: 'pending' | 'priced' | 'declined' | 'timed_out';
    reason: string;
  }[];
  detail: string;
  explanations: {
    insurer_name: string;
    reason: string;
    cover_type?: string | null;
    cover_label?: string | null;
  }[];
}

// ---------------------------------------------------------------------------
// Live updates: the visitor stays on this page, and quotes arrive on it
// ---------------------------------------------------------------------------
//
// Live insurer prices are written while the quote is being started; the
// advisor's hand-entered quotes are added LATER, in the broker portal, onto the
// same request. So the page re-reads its own token-scoped endpoint on a timer
// and new options appear in place -- no reload, no new link.
//
// THE BUDGET. The read runs under the ambient anonymous throttle (200/hour per
// IP). Every 15 s for the first 3 minutes (12 reads), then once a minute up to
// an hour (57 more) keeps a single tab near 70/hour, leaving headroom for the
// visitor's own actions. It pauses while the tab is hidden, stops for good when
// the quote leaves the "open" stage, backs off entirely on a 429, and resumes
// (with a fresh window) when the visitor comes back to the tab.

const POLL_FAST_MS = 15_000;
/** While insurer prices are still arriving (`pricing_summary.state`). */
const POLL_PRICING_MS = 2_000;
const POLL_SLOW_MS = 60_000;
const FAST_WINDOW_MS = 3 * 60_000;
const POLL_WINDOW_MS = 60 * 60_000;

type LiveState = 'on' | 'paused' | 'off';

/** The payload is only trusted as a quote when it has the shape of one. */
function isQuoteData(data: unknown): data is PublicQuoteData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return Array.isArray(d.quotes) && typeof d.customer_state === 'string';
}

/** Can another option still arrive AND still matter to this visitor? */
function isPricing(quote: PublicQuoteData | null): boolean {
  return quote?.pricing_summary?.state === 'pricing';
}

function wantsUpdates(quote: PublicQuoteData): boolean {
  return quote.customer_state === 'open' && !quote.acceptance?.payment_declared;
}

function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

const PAYMENT_MODES = [
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];

/** Label, placeholder and matching upload field per identifier. Keyed by the
 *  API's own field names so the form is driven by `kyc.required_fields` rather
 *  than by a copy of the individual-vs-corporate rule living here too. */
const KYC_FIELDS: Record<
  KycField,
  { label: string; placeholder: string; fileField: string; fileLabel: string }
> = {
  aadhaar_number: {
    label: 'Aadhaar number',
    placeholder: '1234 5678 9012',
    fileField: 'aadhaar_file',
    fileLabel: 'Aadhaar card (PDF or photo)',
  },
  pan_number: {
    label: 'PAN',
    placeholder: 'ABCDE1234F',
    fileField: 'pan_file',
    fileLabel: 'PAN card (PDF or photo)',
  },
  gst_number: {
    label: 'GST number',
    placeholder: '27AAACA1234F1Z5',
    fileField: 'gst_file',
    fileLabel: 'GST certificate (PDF or photo)',
  },
};

/** "For any query, contact us" — a real `tel:` link, or plain text when the
 *  broker has no number on file, so the sentence never becomes a dead link. */
function ContactLine({ quote }: { quote: PublicQuoteData }) {
  if (!quote.contact_phone) {
    return (
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        For any query, contact {quote.contact_name || quote.broker_name}.
      </p>
    );
  }
  return (
    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
      For any query,{' '}
      <a
        href={`tel:${quote.contact_phone}`}
        className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
      >
        contact us
      </a>{' '}
      on {quote.contact_phone}
      {quote.contact_name ? ` (${quote.contact_name})` : ''}.
    </p>
  );
}

/** Whether this page is still listening for new quotes, said plainly. */
function LiveLine({ live, onRefresh }: { live: LiveState; onRefresh: () => void }) {
  if (live === 'on') {
    return (
      <p role="status" className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse" aria-hidden="true" />
        Your advisor may add more quotes here -- this page updates automatically.
      </p>
    );
  }
  if (live === 'paused') {
    return (
      <button
        type="button"
        onClick={onRefresh}
        className="text-xs font-medium text-primary-600 hover:text-primary-700"
      >
        Check for new quotes
      </button>
    );
  }
  return null;
}

function DocumentList({
  documents,
  token,
  emptyLabel,
}: {
  documents: PublicQuoteDocument[];
  token?: string;
  emptyLabel?: string;
}) {
  if (documents.length === 0) {
    return emptyLabel ? <p className="text-sm text-gray-500">{emptyLabel}</p> : null;
  }
  return (
    <ul className="space-y-3">
      {documents.map((doc) => {
        const href = `/policy/api/public/quotes/${token}/documents/${doc.id}/download/`;
        return (
          <li
            key={doc.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex items-center justify-between gap-4"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {doc.name}
              </p>
              <p className="text-xs text-gray-500">{doc.document_type_display}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {/* Queue #219: View asks for `disposition=inline`, so a PDF opens
                  in the browser's own viewer (with its own download button)
                  instead of landing in the downloads folder unread. The server
                  grants inline only for types it considers safe to render, and
                  decides that from the file extension rather than the stored
                  mime_type -- so this link cannot be used to render an uploaded
                  .html on this unauthenticated origin. Download is the same URL
                  without the parameter. */}
              <a
                href={`${href}?disposition=inline`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                View
              </a>
              <a
                href={href}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Download
              </a>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** What the post-acceptance slot is told about the choice it follows. */
export interface AfterAcceptanceContext {
  acceptedOption: PublicQuoteOption;
  state: PublicQuoteState;
  /** Payment already declared: the choice can no longer change. */
  locked: boolean;
  /** The whole payload, and the link it belongs to. */
  quote: PublicQuoteData;
  token: string;
  /** Put a fresh payload the slot received from a write on the page. */
  replaceQuote: (data: PublicQuoteData) => void;
  /** Re-read the payload (e.g. after coming back to the tab). */
  reload: () => Promise<void>;
}

/**
 * THE final page for every public quote -- motor, health and travel alike
 * (owner, 24 Sep). The start forms navigate here with their start response in
 * router state (`StartNotes`); a reload or a shared link carries none and the
 * page is complete without it.
 */
export function PublicQuote() {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const start = startFromState(location.state, token);
  if (!start) return <PublicQuoteView token={token} afterAcceptance={purchaseAfterAcceptance} />;
  // A progressive start answers before any insurer has: its notes list
  // nothing yet, so the page's own (live) explanations stay.
  const startWasFinal = start.pricing.status !== 'pricing';
  return (
    <PublicQuoteView
      token={token}
      afterAcceptance={purchaseAfterAcceptance}
      // The notes already list why an insurer is missing.
      hideExplanations={startWasFinal}
      notice={<StartNotes start={start} />}
      fallback={<StartOptions options={start.pricing.options ?? []} />}
    />
  );
}

/**
 * The quote page itself, addressable by token. `embedded` is the public quote
 * journey drawing it in place under its own confirmation, so the visitor who
 * has just asked for a quote never has to leave the page to see it: the page
 * header and full-screen frame are the journey's, not this one's.
 */
export function PublicQuoteView({
  token,
  embedded = false,
  hideExplanations = false,
  fallback = null,
  afterAcceptance,
  notice = null,
}: {
  token?: string;
  embedded?: boolean;
  /** Drawn at the top of the page -- what the start form was just told. */
  notice?: React.ReactNode;
  /** The embedding page already lists why insurers are missing. */
  hideExplanations?: boolean;
  /** Drawn when an EMBEDDED view cannot load its quote. */
  fallback?: React.ReactNode;
  /** Drawn under the choice once an option has been accepted -- the slot for
   *  a single "Proceed to payment" action. Absent: nothing is drawn. */
  afterAcceptance?: (context: AfterAcceptanceContext) => React.ReactNode;
}) {
  const [quote, setQuote] = useState<PublicQuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Two-step flow: choose an option now, come back and confirm payment later.
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [payment, setPayment] = useState({
    payment_reference: '',
    payment_date: '',
    payment_mode: 'upi',
  });
  const [kycValues, setKycValues] = useState<Record<string, string>>({});
  const [kycFiles, setKycFiles] = useState<Record<string, File | null>>({});
  const [kycErrors, setKycErrors] = useState<Record<string, string>>({});

  const [live, setLive] = useState<LiveState>('off');
  // A poll that STARTED before one of the visitor's own actions must not land
  // after it and put the older payload back. Each action bumps the sequence;
  // a poll whose sequence moved underneath it is discarded.
  const mutationSeq = useRef(0);
  const busyRef = useRef(false);
  const resumeRef = useRef<() => void>(() => {});
  // Whether the last payload said prices are still arriving: the poll then
  // runs every 2 s instead of the advisor-quote cadence.
  const pricingRef = useRef(false);

  const load = useCallback(
    async (initial: boolean): Promise<PublicQuoteData | 'throttled' | null> => {
      if (!token) return null;
      const seq = mutationSeq.current;
      try {
        const response = await axios.get(`/policy/api/public/quotes/${token}/`);
        if (!isQuoteData(response.data)) throw new Error('not a quote payload');
        if (seq === mutationSeq.current && !busyRef.current) {
          setQuote(response.data);
          setError(null);
        }
        pricingRef.current = isPricing(response.data);
        return response.data;
      } catch (err) {
        // The backend excludes draft, cancelled and EXPIRED quotes from this
        // queryset, so a link legitimately stops working when it lapses or when
        // the broker pulls the quote back. "No longer available" covers all of
        // that as well as a bad token -- they are the same thing from the
        // recipient's side, and the page must not say which.
        //
        // Only the FIRST read decides that. A later poll that fails keeps what
        // is already on screen: a dropped request is not a withdrawn quote.
        if (initial) setError('unavailable');
        return statusOf(err) === 429 ? 'throttled' : null;
      } finally {
        if (initial) setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let handle: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let stopped = false;
    let windowStart = Date.now();

    const clear = () => {
      if (handle !== undefined) clearTimeout(handle);
      handle = undefined;
    };
    const hidden = () => typeof document !== 'undefined' && document.hidden;

    const schedule = () => {
      if (cancelled) return;
      clear();
      const elapsed = Date.now() - windowStart;
      if (hidden() || elapsed >= POLL_WINDOW_MS) {
        setLive('paused');
        return;
      }
      setLive('on');
      handle = setTimeout(
        tick,
        pricingRef.current ? POLL_PRICING_MS : elapsed < FAST_WINDOW_MS ? POLL_FAST_MS : POLL_SLOW_MS
      );
    };

    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      const outcome = await load(false);
      inFlight = false;
      if (cancelled) return;
      if (outcome === 'throttled') {
        clear();
        setLive('paused');
        return;
      }
      if (outcome && !wantsUpdates(outcome)) {
        stopped = true;
        clear();
        setLive('off');
        return;
      }
      schedule();
    };

    const resume = () => {
      if (cancelled || stopped || hidden()) return;
      clear();
      windowStart = Date.now();
      void tick();
    };
    resumeRef.current = resume;

    const onVisibility = () => {
      if (hidden()) {
        clear();
        if (!stopped) setLive('paused');
      } else {
        resume();
      }
    };

    setLoading(true);
    void (async () => {
      const first = await load(true);
      if (cancelled) return;
      if (first && first !== 'throttled' && wantsUpdates(first)) {
        schedule();
      } else {
        stopped = !!first && first !== 'throttled';
        setLive(stopped ? 'off' : 'paused');
      }
    })();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', resume);
    return () => {
      cancelled = true;
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', resume);
    };
  }, [token, load]);

  const frame = embedded
    ? 'py-6'
    : 'min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6';

  if (loading) {
    return (
      <div
        className={
          embedded
            ? 'flex items-center justify-center py-10'
            : 'min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900'
        }
      >
        <LoadingSpinner />
      </div>
    );
  }

  if ((error || !quote) && embedded) {
    // The journey has just created this quote, so "no longer available" would
    // be false. Say what is true and keep the way back.
    if (fallback) return <>{fallback}</>;
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-sm text-gray-600 dark:text-gray-400">
        We could not load your quote options just now.{' '}
        {token && (
          <a className="font-medium text-primary-600 hover:text-primary-700" href={`/public/quotes/${token}`}>
            Open your quote page
          </a>
        )}
      </div>
    );
  }

  if ((error || !quote) && notice) {
    // Just started from a form: "no longer available" would be false. What
    // the start said, and any premium it carried, is still true.
    return (
      <div className={frame}>
        <div className="max-w-3xl mx-auto">
          {notice}
          {fallback}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            We could not load your quote options just now. Reload this page in a moment.
          </p>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          This quote link is no longer available
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-md">
          The link may have expired, or the quote may have been withdrawn. Please contact
          your advisor for an up-to-date quote.
        </p>
      </div>
    );
  }

  const optionName = (option: PublicQuoteOption) =>
    coverLabel(option) ? `${coverLabel(option)} \u2014 ${option.insurer_name}` : option.insurer_name;
  const continueLabel = (option: PublicQuoteOption) => {
    const cover = coverLabel(option);
    const premium = optionPremium(option);
    return `Continue with ${option.insurer_name}${cover ? ` \u2014 ${cover}` : ''}${
      premium !== null ? ` \u00b7 ${formatCurrency(premium)}` : ''
    }`;
  };
  const optionDocuments = quote.quotes.filter((o) => (o.documents?.length ?? 0) > 0);

  const acceptance = quote.acceptance;
  const acceptedOption = acceptance
    ? quote.quotes.find((o) => o.id === acceptance.quote)
    : undefined;
  // Once payment is declared the choice is locked -- changing it afterwards
  // would desync what was paid for from what was chosen.
  const locked = !!acceptance?.payment_declared;
  const selectedOption =
    chosen && chosen !== acceptance?.quote ? quote.quotes.find((o) => o.id === chosen) : undefined;

  const state = quote.customer_state;
  // The KYC form appears only after an option is chosen (the API refuses a
  // submission without one) and only when the API would actually accept it --
  // `kyc.can_submit` is the server reading its own transition table, so the
  // form is never drawn for a quote whose every submission would 400.
  // The online purchase journey (`quote.purchase`) replaces the old KYC form
  // and the declare-payment form: its "Your details" step IS the KYC
  // submission, and its one Pay button is the payment. Only when a slot is
  // drawing that journey -- a caller without one keeps the old page.
  // A line with no online path (health, travel) keeps the old forms; the slot
  // only says the advisor will send a payment link.
  // The server decides per ACCEPTED option (`get_purchase`): a hand-typed
  // premium, an insurer with no online purchase, or a customer already through
  // the old KYC form keeps this legacy page exactly as it was.
  const purchaseJourney =
    (quote.purchase?.online === true || quote.purchase?.handoff === true) && !!afterAcceptance;
  const showKycForm = !!acceptance && quote.kyc.can_submit && !purchaseJourney;
  // A quote on a legacy rung can never enter KYC, so it keeps the pre-Wave-D
  // journey: choose an option, then declare the payment made directly to the
  // broker. Taking that away would strand the shared links already in
  // customers' hands on `processing`.
  const legacyPaymentFlow = state === 'open' && !quote.kyc.can_submit;
  const showPaymentForm =
    !!acceptance && !locked && !purchaseJourney && (state === 'awaiting_payment_link' || legacyPaymentFlow);
  // The options stay VISIBLE while the quote is still in flight -- a customer
  // waiting on approval should still be able to see what they chose -- but only
  // CHANGEABLE up to the KYC submission, after which the choice is what the
  // broker is reviewing against. Once the policy exists the pricing table is
  // history and only clutters the page.
  const showOptions = state !== 'policy_available' && state !== 'awaiting_policy';
  const canChangeChoice = !locked && (state === 'open' || state === 'kyc_rejected');
  // Health / travel (24 Sep) have no vehicle and no motor covers: the table is
  // a plan list, and the empty state says the advisor is quoting. An older
  // server that sends no `product_line` is a motor one.
  const productLine = quote.product_line || 'motor';
  const advisorQuoting = quote.pricing_summary?.status === 'advisor_quoting';
  // An insurer's decline is drawn in the comparison itself (owner, 25 Sep:
  // "show people what was declined"); only notes that name no insurer -- e.g.
  // own-damage needing the third-party policy's details -- stay in the box.
  const explanations = quote.pricing_summary?.explanations ?? [];
  const pricingNow = isPricing(quote);
  const pendingUnits = (quote.pricing_summary?.units ?? []).filter((u) => u.state === 'pending');
  const insurerDeclines = explanations.filter((note) => (note.insurer_name || '').trim());
  // With no priced option there is no comparison to draw a decline in, so
  // every note stays in the box.
  const generalNotes =
    quote.quotes.length > 0 ? explanations.filter((note) => !(note.insurer_name || '').trim()) : explanations;

  const slotContent =
    acceptance && acceptedOption && afterAcceptance && token
      ? afterAcceptance({
          acceptedOption,
          state,
          locked,
          quote,
          token,
          replaceQuote: (data: PublicQuoteData) => {
            mutationSeq.current += 1;
            setQuote(data);
          },
          reload: async () => {
            await load(false);
          },
        })
      : null;

  // Re-priced on the customer's choices: the old option ids are gone, so any
  // pending pick is dropped and the payload is re-read in place. The sequence
  // bump discards a poll that started before the re-price.
  const handleRepriced = async () => {
    mutationSeq.current += 1;
    setChosen(null);
    await load(false);
    // Prices are arriving again: back to the fast poll straight away.
    resumeRef.current();
  };
  const showAdjust =
    productLine === 'motor' &&
    state === 'open' &&
    !acceptance &&
    !!token &&
    quote.customise?.can_reprice === true;

  const handleAccept = async () => {
    if (!chosen || !token) return;
    mutationSeq.current += 1;
    busyRef.current = true;
    setBusy(true);
    setActionError(null);
    try {
      const res = await axios.post(`/policy/api/public/quotes/${token}/accept/`, {
        quote: chosen,
      });
      setQuote(res.data);
    } catch {
      setActionError('We could not record your choice. Please try again, or contact your advisor.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleDeclarePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    mutationSeq.current += 1;
    busyRef.current = true;
    setBusy(true);
    setActionError(null);
    try {
      const res = await axios.post(
        `/policy/api/public/quotes/${token}/declare-payment/`,
        payment
      );
      setQuote(res.data);
    } catch {
      setActionError(
        'We could not record your payment details. Please check them and try again.'
      );
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleSubmitKyc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    mutationSeq.current += 1;
    busyRef.current = true;
    setBusy(true);
    setActionError(null);
    setKycErrors({});

    // Multipart because the scans ride along. Only the fields this proposer
    // type was asked for are sent; the API ignores anything else regardless.
    const body = new FormData();
    quote.kyc.required_fields.forEach((field) => {
      body.append(field, kycValues[field] ?? '');
      const fileField = KYC_FIELDS[field].fileField;
      const file = kycFiles[fileField];
      if (file) body.append(fileField, file);
    });

    try {
      const res = await axios.post(`/policy/api/public/quotes/${token}/kyc/`, body);
      setQuote(res.data);
      setKycValues({});
      setKycFiles({});
    } catch (err) {
      // The API answers with per-field messages written for a customer, so they
      // are shown against the inputs rather than collapsed into one toast.
      // `getFieldErrors` already knows the platform's envelope
      // (`{"error": {"message", "fields"}}`) AND the bare `{"detail": ...}` this
      // action returns for "choose an option first" -- re-parsing either here
      // would be a second, drifting copy of that knowledge.
      const fieldErrors = getFieldErrors(err);
      const nonField = fieldErrors[NON_FIELD_KEY];
      setKycErrors(fieldErrors);
      if (nonField) {
        setActionError(nonField);
      } else if (Object.keys(fieldErrors).length === 0) {
        setActionError(getErrorMessage(err));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className={frame}>
      <div className="max-w-3xl mx-auto">
        {notice}
        <header className={embedded ? 'sr-only' : 'mb-8'}>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
            Quote {quote.request_number}
          </p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {quote.insurance_type_name}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Prepared by {quote.broker_name}
            {quote.proposer_name ? ` for ${quote.proposer_name}` : ''}
          </p>
        </header>

        {/* D5 state 3: the policy is here. Nothing else on the page matters as
            much, so it goes first. */}
        {state === 'policy_available' && (
          <section className="mb-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-green-900 dark:text-green-200">
              Your policy is ready
            </h2>
            <p className="mt-1 text-sm text-green-800 dark:text-green-300">
              {quote.customer_message}
            </p>
            <div className="mt-4">
              <DocumentList documents={quote.policy_documents} token={token} />
            </div>
            <ContactLine quote={quote} />
          </section>
        )}

        {/* D5 state 2: issued, copy not attached yet. */}
        {state === 'awaiting_policy' && (
          <section className="mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Your policy is being issued
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {quote.customer_message}
            </p>
            <ContactLine quote={quote} />
          </section>
        )}

        {/* D2's confirmation, in the exact words the API returns. */}
        {state === 'kyc_under_review' && !purchaseJourney && (
          <section className="mb-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-200">
              Documents received
            </h2>
            <p className="mt-1 text-sm text-blue-800 dark:text-blue-300">
              {quote.customer_message}
            </p>
            <ContactLine quote={quote} />
          </section>
        )}

        {state === 'awaiting_payment_link' && !purchaseJourney && (
          <section className="mb-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-green-900 dark:text-green-200">
              KYC approved
            </h2>
            <p className="mt-1 text-sm text-green-800 dark:text-green-300">
              {quote.customer_message}
            </p>
            <ContactLine quote={quote} />
          </section>
        )}

        {state === 'kyc_rejected' && (
          <section className="mb-8 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-200">
              Please send your documents again
            </h2>
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
              {quote.customer_message}
            </p>
            {quote.kyc.rejection_reason && (
              <p className="mt-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                Reason: {quote.kyc.rejection_reason}
              </p>
            )}
            <ContactLine quote={quote} />
          </section>
        )}

        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">Status</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                {quote.status_display}
              </dd>
            </div>
            {quote.registration_number && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Vehicle</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                  {quote.registration_number}
                </dd>
              </div>
            )}
            {quote.sum_insured != null && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Sum Insured</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                  {formatCurrency(Number(quote.sum_insured))}
                </dd>
              </div>
            )}
            {quote.expires_at && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Valid Until</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(quote.expires_at)}
                </dd>
              </div>
            )}
          </dl>
        </section>

        {showOptions && (
          <>
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Your options
              </h2>
              {state === 'open' && (
                <LiveLine live={live} onRefresh={() => resumeRef.current()} />
              )}
            </div>

            {showAdjust && (
              <AdjustQuotePanel
                token={token!}
                options={quote.quotes}
                customise={quote.customise!}
                onRepriced={handleRepriced}
                disabled={pricingNow}
              />
            )}

            {quote.quotes.length === 0 && pendingUnits.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 text-center text-gray-600 dark:text-gray-400">
                {advisorQuoting ? (
                  <div role="status">
                    <p className="font-medium text-gray-900 dark:text-white">
                      Your advisor is preparing quotes &mdash; they will appear here.
                    </p>
                    <p className="mt-2 text-sm">
                      Keep this page open, or come back to this link later. Nothing needs to be
                      done until then.
                    </p>
                  </div>
                ) : quote.pricing_summary && quote.pricing_summary.status !== 'priced' ? (
                  <>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {productLine === 'motor'
                        ? 'No live price could be fetched online for this vehicle.'
                        : 'No live price could be fetched online for this request.'}
                    </p>
                    {quote.pricing_summary.detail && !hideExplanations && (
                      <p className="mt-1 text-sm">{quote.pricing_summary.detail}</p>
                    )}
                    <p className="mt-2 text-sm">
                      Your advisor can add quotes here -- they will appear on this page.
                    </p>
                  </>
                ) : (
                  <>
                    <p>Pricing for this quote is still being prepared.</p>
                    <p className="mt-2 text-sm">
                      Your advisor can add quotes here -- they will appear on this page.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                <QuoteComparison
                  productLine={productLine}
                  options={quote.quotes}
                  declines={insurerDeclines}
                  pending={pendingUnits}
                  selectedId={chosen ?? acceptance?.quote ?? null}
                  canChange={canChangeChoice}
                  onSelect={setChosen}
                />
                {/* Queue #219: each option's own paperwork, named by the
                    option it priced -- never pooled with the request's. */}
                {optionDocuments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Quote documents
                    </h3>
                    {optionDocuments.map((option) => (
                      <div key={option.id}>
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {optionName(option)}
                        </p>
                        <ul className="mt-1 ml-1 space-y-1">
                          {option.documents!.map((doc) => (
                            <li key={doc.id} className="flex items-center gap-2 text-sm">
                              <PaperClipIcon className="h-4 w-4 shrink-0 text-gray-400" />
                              <a
                                href={`/policy/api/public/quotes/${token}/documents/${doc.id}/download/?disposition=inline`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="truncate font-medium text-primary-600 hover:text-primary-700 hover:underline dark:text-primary-400"
                              >
                                {doc.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Why an insurer is missing, in plain words -- kept on the request by
            the server, so it is still here after a reload. Never the adapter's
            raw error. */}
        {showOptions && !hideExplanations && generalNotes.length > 0 && (
          <section
            aria-label="Insurers that could not price online"
            className="mt-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-900 dark:text-amber-200"
          >
            <p className="font-medium">Not priced online</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {generalNotes.map((note, index) => (
                <li key={`${note.insurer_name}-${index}`}>{note.reason}</li>
              ))}
            </ul>
          </section>
        )}

        {actionError && (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {actionError}
          </p>
        )}

        {/* Step one: choose. Available until the KYC has gone in. */}
        {showOptions && quote.quotes.length > 0 && canChangeChoice && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {/* ONE action for the whole comparison: pick a cell above, then
                continue. The label names exactly what will be accepted. */}
            {/* Once a choice is made and nothing new is picked, the disabled
                "Select an option above" button only confused people: say what
                was chosen and how to change it instead. */}
            {!selectedOption && acceptance && acceptedOption ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                You chose <span className="font-medium">{optionName(acceptedOption)}</span>. To
                change it, pick another price above.
              </p>
            ) : (
              <button
                type="button"
                onClick={handleAccept}
                disabled={!selectedOption || busy}
                className="px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectedOption ? continueLabel(selectedOption) : 'Select an option above'}
              </button>
            )}
          </div>
        )}

        {/* Post-acceptance slot: whatever the embedding page wants to offer
            once an option is accepted (a later lane: one common "Proceed to
            payment" that the server routes to the chosen insurer). Nothing is
            drawn here unless a caller supplies it. */}
        {slotContent != null && slotContent !== false && (
          <div className="mt-4" data-testid="after-acceptance-slot">
            {slotContent}
          </div>
        )}

        {/* Step two: KYC. The fields come from the API's `required_fields`, which
            branches on the proposer type -- Aadhaar + PAN for an individual,
            PAN + GST for a company or firm. */}
        {showKycForm && (
          <section className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Complete your KYC
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {quote.broker_name} needs these details before your policy can be issued.
            </p>
            <form onSubmit={handleSubmitKyc} className="mt-4 space-y-5">
              {quote.kyc.required_fields.map((field) => {
                const config = KYC_FIELDS[field];
                if (!config) return null;
                return (
                  <div key={field} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor={`kyc-${field}`}
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        {config.label}
                      </label>
                      <input
                        id={`kyc-${field}`}
                        type="text"
                        required
                        value={kycValues[field] ?? ''}
                        placeholder={config.placeholder}
                        onChange={(e) =>
                          setKycValues((v) => ({ ...v, [field]: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      {kycErrors[field] && (
                        <p className="mt-1 text-sm text-red-600" role="alert">
                          {kycErrors[field]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor={`kyc-${config.fileField}`}
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        {config.fileLabel}
                      </label>
                      <input
                        id={`kyc-${config.fileField}`}
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={(e) =>
                          setKycFiles((f) => ({
                            ...f,
                            [config.fileField]: e.target.files?.[0] ?? null,
                          }))
                        }
                        className="w-full text-sm text-gray-600 dark:text-gray-300"
                      />
                      {kycErrors[config.fileField] && (
                        <p className="mt-1 text-sm text-red-600" role="alert">
                          {kycErrors[config.fileField]}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              <button
                type="submit"
                disabled={busy}
                className="px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
              >
                Submit documents
              </button>
            </form>
          </section>
        )}

        {/* Step three: tell the broker about a payment made directly to them.
            Only once the KYC is approved -- the payment link the customer is
            promised is not sent before that, so offering the form earlier would
            invite a payment the broker cannot yet act on. */}
        {showPaymentForm && (
          <section className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Already paid?
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Pay {quote.broker_name} directly, then enter the details below so they can issue
              your policy. Nothing is charged on this page.
            </p>
            <form onSubmit={handleDeclarePayment} className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Payment reference / UTR
                </label>
                <input
                  type="text"
                  required
                  value={payment.payment_reference}
                  onChange={(e) =>
                    setPayment((p) => ({ ...p, payment_reference: e.target.value }))
                  }
                  placeholder="Transaction reference from your bank or UPI app"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date paid
                </label>
                <input
                  type="date"
                  required
                  max={new Date().toISOString().slice(0, 10)}
                  value={payment.payment_date}
                  onChange={(e) => setPayment((p) => ({ ...p, payment_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  How you paid
                </label>
                <select
                  value={payment.payment_mode}
                  onChange={(e) => setPayment((p) => ({ ...p, payment_mode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                >
                  I have paid
                </button>
              </div>
            </form>
          </section>
        )}

        {/* Confirmed. Nothing further for the customer to do. */}
        {locked && acceptance && state !== 'policy_available' && (
          <section className="mt-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-green-900 dark:text-green-200">
              Payment confirmed with {quote.broker_name}
            </h2>
            <p className="mt-1 text-sm text-green-800 dark:text-green-300">
              You chose {acceptedOption?.insurer_name ?? 'your selected option'} and reported
              payment {acceptance.payment_reference}
              {acceptance.payment_date ? ` on ${formatDate(acceptance.payment_date)}` : ''}.
              {' '}They will confirm receipt and issue your policy — you will be able to download
              it once it is ready.
            </p>
          </section>
        )}

        {/* Attachments that belong to the REQUEST rather than to one price --
            a covering letter, say. Queue #219 moved each premium option's own
            document up beside that option; what is left here has nowhere else
            to go, and the block disappears entirely when there is nothing.

            The API returns only the customer-facing types, and re-checks that
            allowlist on download, so a link forwarded onward still cannot reach
            the KYC or ID documents on the same quote. The issued policy is not
            in this list -- it has its own section above. */}
        {quote.documents?.length > 0 && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-3">
              Documents
            </h2>
            <DocumentList documents={quote.documents} token={token} />
          </>
        )}

        <p className="mt-8 text-center text-xs text-gray-400">
          Questions? Contact {quote.contact_name || quote.broker_name}.
        </p>
      </div>
    </div>
  );
}
